import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Eye, EyeOff, Briefcase, X, ChevronLeft } from 'lucide-react';
import { getExchangeRates } from 'api/walletApi';
import { sendNotfication, encode, decode } from 'utils/Helper';
import socket from 'utils/Socket';
import C from 'utils/Constant';
import Cookies from 'js-cookie';
import storage from 'utils/Storage';
import { API } from 'utils/Helper';

const HeaderBarContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #fff;
  font-weight: bold;
  font-size: 13px;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  color: #a0aeb5;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  &:hover {
    color: #fff;
  }
`;

const BalanceContainer = styled.div`
  padding: 0 16px 12px 16px;
`;

const EstimatedText = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #a0aeb5;
  font-size: 11px;
`;

const BigBalance = styled.div`
  color: #fff;
  font-weight: 800;
  font-size: 24px;
  margin-top: 2px;
  
  span {
    color: #a0aeb5;
    font-size: 16px;
    font-weight: bold;
    margin-left: 4px;
  }
`;

const CurrenciesBox = styled.div`
  margin-top: 12px;
  background-color: rgba(30, 32, 36, 0.6);
  border-radius: 6px;
  padding: 10px 12px;
`;

const CurrencyBoxHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11px;
  color: #a0aeb5;
  font-weight: 600;
  padding-bottom: 6px;
  border-bottom: 1px solid #2a2e33;
`;

const CurrencyRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 8px;
`;

const CurrencyInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CurrencyIcon = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: 11px;
  font-weight: bold;
  color: ${props => props.darkText ? '#000' : '#fff'};
  background: ${props => props.bg || '#f0b90b'};
`;

const CurrencyNameBlock = styled.div`
  display: flex;
  flex-direction: column;
`;

const CodeText = styled.div`
  color: #fff;
  font-weight: bold;
  font-size: 13px;
`;

const NameText = styled.div`
  color: #a0aeb5;
  font-size: 11px;
`;

const CurrencyValueBlock = styled.div`
  text-align: right;
`;

const BalanceAmount = styled.div`
  color: #fff;
  font-weight: bold;
  font-size: 13px;
`;

const BalanceUsd = styled.div`
  color: #a0aeb5;
  font-size: 11px;
`;

const ActionButtons = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 12px;
`;

const BlueBtn = styled.button`
  height: 36px;
  border-radius: 6px;
  background-color: #1475e1;
  border: none;
  color: #fff;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #1a84f3;
  }

  &:disabled {
    background-color: #2a2e33;
    color: #4f575e;
    cursor: not-allowed;
  }
`;

const DarkBtn = styled.button`
  height: 36px;
  border-radius: 6px;
  background-color: #1e2024;
  border: none;
  color: #fff;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  margin-top: 10px;
  width: 100%;
  transition: all 0.2s;
  
  &:hover {
    background-color: #2a2e33;
  }
`;

const TwoFAFooter = styled.div`
  padding: 0 16px 16px 16px;
`;

const TwoFAText = styled.p`
  color: #a0aeb5;
  font-size: 11px;
  margin-bottom: 6px;
`;

const ModalContent = styled.div`
  background-color: #1a2c38;
  border-radius: 8px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

/* Swap View Specific Styled Components */
const SwapInputContainer = styled.div`
  margin-top: 10px;
  background-color: rgba(30, 32, 36, 0.6);
  border-radius: 6px;
  padding: 10px 12px;
`;

const SwapInputHeader = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #a0aeb5;
  margin-bottom: 6px;
`;

const SwapInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SwapAmountInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  color: #fff;
  font-size: 16px;
  font-weight: bold;
  outline: none;
  &::placeholder {
    color: #4f575e;
  }
`;

const SwapSelect = styled.select`
  background-color: #1a2c38;
  color: #fff;
  border: 1px solid #2a2e33;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  outline: none;
  &:focus {
    border-color: #1475e1;
  }
`;

const ExchangeDetailsBox = styled.div`
  margin-top: 12px;
  background-color: rgba(30, 32, 36, 0.3);
  border: 1px dashed #2a2e33;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 11px;
  color: #a0aeb5;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ExchangeDetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  span.value {
    color: #fff;
    font-weight: bold;
  }
`;

export default function WalletModalHome({
  onDeposit,
  onWithdraw,
  onClose,
  estimatedTotalUsd,
  currencies,
  allCurrencies = [],
  currencyRates = {},
  initialStep = 'home'
}) {
  const [hideBalance, setHideBalance] = useState(true);
  const [step, setStep] = useState(initialStep); // 'home' or 'swap'
  const [is2faEnabled, setIs2faEnabled] = useState(false);

  useEffect(() => {
    if (initialStep) {
      setStep(initialStep);
    }
  }, [initialStep]);

  useEffect(() => {
    let uid = Cookies.get('uid');
    if (!uid) {
      try {
        const token = storage.getKey('token');
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          uid = payload.id || payload.uid;
        }
      } catch (e) {}
    }
    if (uid) {
      fetch(`${API}/2fa/status/${uid}`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setIs2faEnabled(data.is2faEnabled);
          }
        })
        .catch(err => console.error("Error fetching 2FA status:", err));
    }
  }, []);
  const [fromCoin, setFromCoin] = useState('USDT');
  const [toCoin, setToCoin] = useState('INR');
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  const [rates, setRates] = useState({});
  const [loading, setLoading] = useState(false);

  const getCoinRateInUsd = (coinCode, currentRates) => {
    const code = String(coinCode || "").toUpperCase();
    if (code === 'INR') {
      const inrRate = currencyRates?.INR || 95.60;
      return 1 / inrRate;
    }
    if (['USDT', 'USDC', 'BUSD', 'TUSD', 'USDP', 'USDTBSC', 'USD'].includes(code)) {
      return 1.0;
    }
    return currentRates[code] !== undefined ? currentRates[code] : null;
  };

  const getActiveExchangeRate = (fCoin, tCoin, currentRates) => {
    const fRate = getCoinRateInUsd(fCoin, currentRates);
    const tRate = getCoinRateInUsd(tCoin, currentRates);
    if (fRate === null || tRate === null) return null;
    return tRate > 0 ? fRate / tRate : null;
  };

  const exchangeRate = getActiveExchangeRate(fromCoin, toCoin, rates);

  const isStableToStable = (fCoin, tCoin) => {
    const stables = ['USDT', 'USDC', 'BUSD', 'TUSD', 'USDP', 'USDTBSC', 'USD'];
    return stables.includes(fCoin.toUpperCase()) && stables.includes(tCoin.toUpperCase());
  };

  // Fetch exchange rates when entering swap view
  useEffect(() => {
    if (step === 'swap') {
      getExchangeRates()
        .then(res => {
          setRates(res || {});
        })
        .catch(err => {
          console.error("Error fetching exchange rates:", err);
        });
    }
  }, [step]);

  useEffect(() => {
    if (fromAmount) {
      const rate = getActiveExchangeRate(fromCoin, toCoin, rates);
      if (rate !== null) {
        const rawAmount = parseFloat(fromAmount) * rate;
        setToAmount((rawAmount * 0.998).toFixed(4));
      } else {
        setToAmount('');
      }
    }
  }, [fromCoin, toCoin, rates, currencyRates]);

  // Listen for socket submit swap response
  useEffect(() => {
    const handleResponse = (data) => {
      const decoded = decode(data);
      setLoading(false);
      if (decoded && decoded.status) {
        const isSuccess = decoded.status.toLowerCase().includes("swapped") || decoded.status.toLowerCase().includes("successful");
        const type = isSuccess ? "success" : "danger";
        sendNotfication(decoded.status, type, "top-right");
        if (isSuccess) {
          setStep('home');
          setFromAmount('');
          setToAmount('');
          // Refresh credit/balance
          socket.emit(C.CREDIT);
        }
      }
    };
    socket.on(C.SUBMIT_NEW_SWAP, handleResponse);
    return () => {
      socket.off(C.SUBMIT_NEW_SWAP, handleResponse);
    };
  }, []);

  const [defaultCoinsSet, setDefaultCoinsSet] = useState(false);

  // Update default coins based on available currencies
  useEffect(() => {
    if (allCurrencies && allCurrencies.length > 0 && !defaultCoinsSet) {
      const codes = allCurrencies.map(c => c.code);
      if (codes.includes('USDT')) setFromCoin('USDT');
      else setFromCoin(codes[0]);

      if (codes.includes('INR')) setToCoin('INR');
      else setToCoin(codes[codes.length - 1] || codes[0]);

      setDefaultCoinsSet(true);
    }
  }, [allCurrencies, defaultCoinsSet]);

  const handleCoinChange = (type, value) => {
    if (type === 'from') {
      setFromCoin(value);
    } else {
      setToCoin(value);
    }
  };

  const handleFromAmountChange = (e) => {
    const val = e.target.value;
    if (val === '' || /^\d*\.?\d*$/.test(val)) {
      setFromAmount(val);
      if (val === '') {
        setToAmount('');
      } else {
        if (exchangeRate !== null) {
          const rawAmount = parseFloat(val) * exchangeRate;
          setToAmount((rawAmount * 0.998).toFixed(4));
        } else {
          setToAmount('');
        }
      }
    }
  };

  const handleToAmountChange = (e) => {
    const val = e.target.value;
    if (val === '' || /^\d*\.?\d*$/.test(val)) {
      setToAmount(val);
      if (val === '') {
        setFromAmount('');
      } else {
        if (exchangeRate !== null && exchangeRate > 0) {
          const rate = 1 / exchangeRate;
          const rawAmount = parseFloat(val) / 0.998;
          setFromAmount((rawAmount * rate).toFixed(4));
        } else {
          setFromAmount('');
        }
      }
    }
  };

  const handleSwapSubmit = () => {
    const numFrom = parseFloat(fromAmount);
    const numTo = parseFloat(toAmount);
    if (!numFrom || numFrom <= 0) {
      sendNotfication("Please enter a valid amount.", "danger", "top-right");
      return;
    }
    if (fromCoin === toCoin) {
      sendNotfication("Cannot swap same coin.", "danger", "top-right");
      return;
    }
    const fromBalanceObj = allCurrencies.find(c => c.code === fromCoin);
    const fromBalanceVal = fromBalanceObj ? fromBalanceObj.balance : 0;
    if (numFrom > fromBalanceVal) {
      sendNotfication("Insufficient balance.", "danger", "top-right");
      return;
    }

    setLoading(true);
    socket.emit(
      C.SUBMIT_NEW_SWAP,
      encode({
        amount: numFrom,
        amount1: numTo,
        coin: fromCoin,
        coin1: toCoin,
        immed: 0,
      })
    );
  };

  // Find balances for selected coins
  const fromBalanceObj = allCurrencies.find(c => c.code === fromCoin);
  const fromBalance = fromBalanceObj ? fromBalanceObj.formattedBalance : '0.00';
  const toBalanceObj = allCurrencies.find(c => c.code === toCoin);
  const toBalance = toBalanceObj ? toBalanceObj.formattedBalance : '0.00';

  if (step === 'swap') {
    return (
      <ModalContent>
        <HeaderBarContainer>
          <HeaderTitle>
            <IconButton onClick={() => setStep('home')}>
              <ChevronLeft className="w-4 h-4" />
            </IconButton>
            <span style={{ marginLeft: '6px' }}>Swap Currency</span>
          </HeaderTitle>
          <IconButton onClick={onClose}>
            <X className="w-4 h-4" />
          </IconButton>
        </HeaderBarContainer>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          <BalanceContainer>
            <SwapInputContainer>
              <SwapInputHeader>
                <span>You send</span>
                <span>Balance: {fromBalance} {fromCoin}</span>
              </SwapInputHeader>
              <SwapInputRow>
                <SwapAmountInput
                  type="text"
                  placeholder="0.00"
                  value={fromAmount}
                  onChange={handleFromAmountChange}
                />
                <SwapSelect value={fromCoin} onChange={(e) => handleCoinChange('from', e.target.value)}>
                  {allCurrencies.map(c => (
                    <option key={c.code} value={c.code}>{c.code}</option>
                  ))}
                </SwapSelect>
              </SwapInputRow>
            </SwapInputContainer>

            <SwapInputContainer style={{ marginTop: '8px' }}>
              <SwapInputHeader>
                <span>You get approximately</span>
                <span>Balance: {toBalance} {toCoin}</span>
              </SwapInputHeader>
              <SwapInputRow>
                <SwapAmountInput
                  type="text"
                  placeholder="0.00"
                  value={toAmount}
                  onChange={handleToAmountChange}
                />
                <SwapSelect value={toCoin} onChange={(e) => handleCoinChange('to', e.target.value)}>
                  {allCurrencies.map(c => (
                    <option key={c.code} value={c.code}>{c.code}</option>
                  ))}
                </SwapSelect>
              </SwapInputRow>
            </SwapInputContainer>

            <ExchangeDetailsBox>
              <ExchangeDetailRow>
                <span>Exchange Rate</span>
                <span className="value">
                  {exchangeRate !== null ? (
                    `1 ${fromCoin} = ${exchangeRate.toFixed(4)} ${toCoin}`
                  ) : (
                    <span style={{ color: '#ff4d4f' }}>Swap Not Available</span>
                  )}
                </span>
              </ExchangeDetailRow>
              <ExchangeDetailRow>
                <span>Swap Fee</span>
                <span className="value">0.2%</span>
              </ExchangeDetailRow>
              <ExchangeDetailRow>
                <span>Estimated Payout</span>
                <span className="value" style={{ color: exchangeRate !== null ? '#24ee89' : '#ff4d4f' }}>
                  {exchangeRate !== null ? `${toAmount || '0.00'} ${toCoin}` : 'N/A'}
                </span>
              </ExchangeDetailRow>
            </ExchangeDetailsBox>

            <BlueBtn
              style={{ width: '100%', marginTop: '12px' }}
              onClick={handleSwapSubmit}
              disabled={loading || !fromAmount || parseFloat(fromAmount) <= 0 || exchangeRate === null || fromCoin === toCoin}
            >
              {loading ? 'Swapping...' : (fromCoin === toCoin ? 'Cannot Swap Same Coin' : (exchangeRate === null ? 'Swap Not Available' : 'Swap Now'))}
            </BlueBtn>
          </BalanceContainer>
        </div>
      </ModalContent>
    );
  }

  return (
    <ModalContent>
      <HeaderBarContainer>
        <HeaderTitle>
          <Briefcase className="w-4 h-4" />
          <span>Wallet</span>
        </HeaderTitle>
        <IconButton onClick={onClose}>
          <X className="w-4 h-4" />
        </IconButton>
      </HeaderBarContainer>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        <BalanceContainer>
          <EstimatedText>
            <span>Estimated balance</span>
            <span style={{ cursor: "pointer", display: "flex", alignItems: "center" }} onClick={() => setHideBalance(!hideBalance)}>
              {hideBalance ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </span>
          </EstimatedText>
          <BigBalance>
            {hideBalance ? "******" : `$${estimatedTotalUsd}`} <span>USD</span>
          </BigBalance>

          {currencies && currencies.length > 0 && (
            <CurrenciesBox>
              <CurrencyBoxHeader>
                <span>Your currencies</span>
                <span>Value</span>
              </CurrencyBoxHeader>
              <div style={{ maxHeight: "120px", overflowY: "auto", marginTop: "4px" }}>
                {currencies.map((c, i) => (
                  <CurrencyRow key={i}>
                    <CurrencyInfo>
                      <CurrencyIcon bg={c.color} darkText={c.code === 'INR'}>
                        {c.symbol || c.code.charAt(0)}
                      </CurrencyIcon>
                      <CurrencyNameBlock>
                        <CodeText>{c.code}</CodeText>
                        <NameText>{c.name}</NameText>
                      </CurrencyNameBlock>
                    </CurrencyInfo>
                    <CurrencyValueBlock>
                      <BalanceAmount>{hideBalance ? "******" : c.balance}</BalanceAmount>
                      <BalanceUsd>{hideBalance ? "****** USD" : `$${c.valueInUsd} USD`}</BalanceUsd>
                    </CurrencyValueBlock>
                  </CurrencyRow>
                ))}
              </div>
            </CurrenciesBox>
          )}

          <ActionButtons>
            <BlueBtn onClick={onDeposit}>Deposit</BlueBtn>
            <BlueBtn onClick={onWithdraw}>Withdraw</BlueBtn>
          </ActionButtons>
          <DarkBtn onClick={() => setStep('swap')}>Swap Currency</DarkBtn>
        </BalanceContainer>

        <TwoFAFooter>
          <TwoFAText>Improve your account security with Two-Factor Authentication</TwoFAText>
          {is2faEnabled ? (
            <DarkBtn style={{ marginTop: 0, color: '#1fc16b', cursor: 'default' }} disabled>✓ 2FA Enabled</DarkBtn>
          ) : (
            <DarkBtn style={{ marginTop: 0 }} onClick={() => {
              onClose();
              window.location.href = '/settings/security';
            }}>Enable 2FA</DarkBtn>
          )}
        </TwoFAFooter>
      </div>
    </ModalContent>
  );
}
